import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, InputAdornment, CircularProgress, Paper, Stack, Button, Avatar } from '@mui/material';
import { Search, Diversity3 as ClubIcon, Groups as GroupIcon, HowToReg } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { clubService, DiveClub } from '../services/clubService';
import { groupService, DiveGroup } from '../services/groupService';
import { useNotification } from './NotificationContext';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

// DiveGroup/DiveClub are structurally identical apart from the id field name
// (groupId/clubId) — normalize to this shape so one panel can render either.
interface Entity {
  id: string;
  name: string;
  memberCount: number;
}

type Kind = 'club' | 'group';

const KIND_CONFIG: Record<Kind, {
  noun: string;
  placeholder: string;
  icon: React.ReactElement;
  search: (query: string) => Promise<Entity[]>;
  requestJoin: (id: string) => Promise<unknown>;
  detailPath: (id: string) => string;
}> = {
  club: {
    noun: 'Club',
    placeholder: 'Search clubs by name…',
    icon: <ClubIcon sx={{ fontSize: 16 }} />,
    search: async (query) => (await clubService.searchClubs(query)).map((c: DiveClub) => ({ id: c.clubId, name: c.name, memberCount: c.memberCount })),
    requestJoin: (id) => clubService.requestJoin(id),
    detailPath: (id) => `/clubs/${id}`,
  },
  group: {
    noun: 'Group',
    placeholder: 'Search groups by name…',
    icon: <GroupIcon sx={{ fontSize: 16 }} />,
    search: async (query) => (await groupService.searchGroups(query)).map((g: DiveGroup) => ({ id: g.groupId, name: g.name, memberCount: g.memberCount })),
    requestJoin: (id) => groupService.requestJoin(id),
    detailPath: (id) => `/groups/${id}`,
  },
};

/**
 * Compact "find a group/club" search-and-request-to-join surface. Originally club-only
 * (DiscoverClubsPanel, shared by the profile and feed pages); generalized here so groups get
 * the same discovery surface clubs already had via GET /clubs/search — groups previously had
 * no way to be found beyond an exact ShareCode or an already-known groupId.
 */
const DiscoverGroupsOrClubsPanel: React.FC<{ kind: Kind; initialQuery?: string }> = ({ kind, initialQuery = '' }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const config = KIND_CONFIG[kind];
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<Entity[]>([]);
  const [searching, setSearching] = useState(false);
  const [requestingId, setRequestingId] = useState<string | null>(null);
  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());

  // Pre-run the search when opened with a query (e.g. from a region page).
  useEffect(() => {
    if (initialQuery.trim().length < 2) return;
    let cancelled = false;
    setSearching(true);
    config.search(initialQuery)
      .then(r => { if (!cancelled) setResults(r); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery, kind]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      setResults(await config.search(value));
    } finally {
      setSearching(false);
    }
  };

  const handleRequestJoin = async (entity: Entity) => {
    setRequestingId(entity.id);
    try {
      await config.requestJoin(entity.id);
      setRequestedIds(prev => new Set(prev).add(entity.id));
      showSuccess(`Requested to join "${entity.name}"`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to request to join');
    } finally {
      setRequestingId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Find a Dive {config.noun}</Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={config.placeholder}
        value={query}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searching ? <CircularProgress size={16} /> : <Search fontSize="small" />}
            </InputAdornment>
          ),
        }}
        autoComplete="off"
      />

      {results.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {results.map(entity => (
            <Stack
              key={entity.id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ p: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Avatar
                sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 12, cursor: 'pointer' }}
                onClick={() => navigate(config.detailPath(entity.id))}
              >
                {initials(entity.name) || config.icon}
              </Avatar>
              <Box
                sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
                onClick={() => navigate(config.detailPath(entity.id))}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{entity.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {entity.memberCount} member{entity.memberCount === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={requestingId === entity.id ? <CircularProgress size={14} /> : <HowToReg />}
                disabled={requestingId === entity.id || requestedIds.has(entity.id)}
                onClick={() => handleRequestJoin(entity)}
                sx={{ minHeight: 36 }}
              >
                {requestedIds.has(entity.id) ? 'Requested' : 'Request to join'}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}

      {query.trim().length >= 2 && !searching && results.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          No {config.noun.toLowerCase()}s found for "{query}"
        </Typography>
      )}
    </Paper>
  );
};

export default DiscoverGroupsOrClubsPanel;
